import { Web3Provider } from '@ethersproject/providers';
import { useEffect, useState } from 'react';
import { Currency } from '@manifoldxyz/js-ts-utils';

interface ERC20Metadata {
  symbol: string;
  decimals: number;
  name?: string;
}

interface UseERC20MetadataResult {
  erc20Metadata: ERC20Metadata | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

interface UseERC20MetadataParams {
  networkId: number;
  address: string;
}

export function useERC20Metadata({
  networkId,
  address,
}: UseERC20MetadataParams): UseERC20MetadataResult {
  const [erc20Metadata, setERC20Metadata] = useState<ERC20Metadata | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchMetadata = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const metadata = await Currency.getERC20Metadata(
        networkId,
        address,
        window.ManifoldEthereumProvider.provider() as Web3Provider,
      );
      setERC20Metadata(metadata);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch ERC20 metadata'));
      setERC20Metadata(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMetadata();
  }, [networkId, address]);

  return {
    erc20Metadata,
    isLoading,
    error,
    refetch: fetchMetadata,
  };
}
